import { gql } from '@apollo/client';

// ==================== AUTH/MEMBERSHIPS ====================
export const GET_MY_AUTH_CONTEXT = gql`
  query GetMyAuthContext {
    getMyAuthContext {
      userId
      email
      legacyRole
      highestRole
      isAdmin
      memberships {
        id
        role
        isActive
        organization {
          id
          name
        }
        parkingIds
        parkings {
          id
          name
          code
        }
      }
    }
  }
`;

export const GET_MY_MEMBERSHIPS = gql`
  query GetMyMemberships {
    getMyMemberships {
      id
      role
      isActive
      organization {
        id
        name
      }
      parkingIds
      parkings {
        id
        name
        code
      }
      createdAt
      createdBy {
        id
        email
      }
    }
  }
`;

export const GET_ORG_MEMBERSHIPS = gql`
  query GetOrgMemberships($organizationId: ID!) {
    getOrgMemberships(organizationId: $organizationId) {
      id
      role
      isActive
      user {
        id
        email
        firstName
        lastName
      }
      parkingIds
      parkings {
        id
        name
        code
      }
      createdAt
    }
  }
`;

export const GET_PARKING_STAFF = gql`
  query GetParkingStaff($parkingId: ID!) {
    getParkingStaff(parkingId: $parkingId) {
      id
      role
      isActive
      user {
        id
        email
        firstName
        lastName
      }
      parkingIds
      createdAt
    }
  }
`;

export const GET_USER_MEMBERSHIPS = gql`
  query GetUserMemberships($userId: ID!) {
    getUserMemberships(userId: $userId) {
      id
      role
      isActive
      organization {
        id
        name
      }
      parkingIds
      parkings {
        id
        name
        code
      }
      createdAt
    }
  }
`;

export const GET_ACTIVE_SESSIONS_FOR_PARKING = gql`
  query GetActiveSessionsForParking($parkingId: ID!) {
    getActiveSessionsForParking(parkingId: $parkingId) {
      id
      status
      sessionType
      billingType
      discountPercent
      plateNumber
      notes
      reservedAt
      startedAt
      durationMinutes
      calculatedFee
      finalFee
      location {
        id
        name
        code
      }
      user {
        id
        email
        phone
        firstName
        lastName
      }
      vehicle {
        id
        plateNumber
      }
      createdByStaff {
        id
        email
      }
    }
  }
`;

export const GET_SLOTS_TELEMETRY = gql`
  query GetSlotsTelemetry($parkingId: ID!) {
    getSlotsTelemetry(parkingId: $parkingId) {
      slotId
      deviceId
      barrierStatus
      vehicleDetected
      hasFault
      faults
      batteryVoltage
      lastUpdated
    }
  }
`;

// Overview mode - all parkings user has access to
export const GET_ALL_ACTIVE_SESSIONS = gql`
  query GetAllActiveSessions {
    getAllActiveSessions {
      id
      status
      sessionType
      billingType
      discountPercent
      plateNumber
      notes
      reservedAt
      startedAt
      durationMinutes
      calculatedFee
      finalFee
      location {
        id
        name
        code
      }
      user {
        id
        email
        phone
        firstName
        lastName
      }
      vehicle {
        id
        plateNumber
      }
      createdByStaff {
        id
        email
      }
    }
  }
`;

export const GET_ALL_SLOTS_TELEMETRY = gql`
  query GetAllSlotsTelemetry {
    getAllSlotsTelemetry {
      slotId
      deviceId
      barrierStatus
      vehicleDetected
      hasFault
      faults
      batteryVoltage
      lastUpdated
    }
  }
`;

export const GET_DEVICE_TELEMETRY_HISTORY = gql`
  query GetDeviceTelemetryHistory($slotId: ID!, $limit: Int, $hoursBack: Int) {
    getDeviceTelemetryHistory(slotId: $slotId, limit: $limit, hoursBack: $hoursBack) {
      id
      deviceId
      lockStatus
      vehicleDetected
      batteryVoltage
      batteryFault
      motorFault
      magnetFault
      radarFault
      coilFrequency
      receivedAt
    }
  }
`;

export const GET_DEVICE_COMMAND_HISTORY = gql`
  query GetDeviceCommandHistory($deviceId: Int!, $limit: Int, $hoursBack: Int) {
    getDeviceCommandHistory(deviceId: $deviceId, limit: $limit, hoursBack: $hoursBack) {
      id
      deviceId
      command
      commandName
      status
      sentAt
      acknowledgedAt
      responseCode
      responseMessage
      createdAt
    }
  }
`;

export const GET_SLOT_SESSION_HISTORY = gql`
  query GetSlotSessionHistory($slotId: ID!, $limit: Int, $hoursBack: Int) {
    getSlotSessionHistory(slotId: $slotId, limit: $limit, hoursBack: $hoursBack) {
      id
      status
      sessionType
      billingType
      barrierLoweredOnCreate
      plateNumber
      notes
      createdAt
      reservedAt
      startedAt
      endedAt
      durationMinutes
      finalFee
      createdByStaff {
        id
        email
        firstName
        lastName
      }
      user {
        id
        email
        phone
        firstName
        lastName
      }
    }
  }
`;

export const GET_SLOT_SESSION_EVENTS = gql`
  query GetSlotSessionEvents($slotId: ID!, $limit: Int, $hoursBack: Int) {
    getSlotSessionEvents(slotId: $slotId, limit: $limit, hoursBack: $hoursBack) {
      id
      sessionId
      previousStatus
      newStatus
      trigger
      reason
      triggeredBy
      metadata
      createdAt
    }
  }
`;

// Pending payment sessions (MANUAL billing that need staff confirmation)
export const GET_PENDING_PAYMENT_SESSIONS = gql`
  query GetPendingPaymentSessions($hoursBack: Int) {
    getPendingPaymentSessions(hoursBack: $hoursBack) {
      id
      status
      sessionType
      billingType
      discountPercent
      plateNumber
      notes
      reservedAt
      startedAt
      endedAt
      durationMinutes
      calculatedFee
      finalFee
      location {
        id
        name
        code
        parent {
          id
          name
          parent {
            id
            name
          }
        }
      }
      user {
        id
        email
        phone
        firstName
        lastName
      }
      vehicle {
        id
        plateNumber
      }
      createdByStaff {
        id
        email
      }
    }
  }
`;

// ==================== STAFF SEARCH ====================
export const SEARCH_USER_BY_PHONE_OR_EMAIL = gql`
  query SearchUserByPhoneOrEmail($phone: String, $email: String, $organizationId: ID!) {
    searchUserByPhoneOrEmail(phone: $phone, email: $email, organizationId: $organizationId) {
      id
      email
      phone
      firstName
      lastName
    }
  }
`;

// ==================== DASHBOARD ====================
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats {
    getDashboardStats {
      totalUsers
      totalOrganizations
      totalParkings
      totalSlots
      availableSlots
      activeSessions
      todayRevenue
      monthlyRevenue
      todayCardRevenue
      todayCardRevenuePercent
      todayCashRevenue
      todayCashRevenuePercent
      monthlyCardRevenue
      monthlyCardRevenuePercent
      monthlyCashRevenue
      monthlyCashRevenuePercent
    }
  }
`;

// ==================== USERS ====================
export const GET_ALL_USERS = gql`
  query GetAllUsers($organizationId: ID) {
    getAllUsers(organizationId: $organizationId) {
      id
      email
      phone
      firstName
      lastName
      address
      role
      enabled
      emailVerified
      phoneVerified
      lastLogin
      createdAt
      updatedAt
      organization {
        id
        name
      }
    }
  }
`;

// ==================== ORGANIZATIONS ====================
export const GET_ALL_ORGANIZATIONS = gql`
  query GetAllOrganizations {
    getAllOrganizations {
      id
      name
      email
      phone
      address
      revenueShareRate
      enabled
      createdAt
      updatedAt
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query GetOrganization($id: ID!) {
    getOrganization(id: $id) {
      id
      name
      email
      phone
      address
      revenueShareRate
      enabled
      createdAt
      updatedAt
    }
  }
`;

// ==================== LOCATIONS ====================
export const GET_ALL_LOCATIONS = gql`
  query GetAllLocations($type: LocationType, $organizationId: ID, $parentId: ID) {
    getAllLocations(type: $type, organizationId: $organizationId, parentId: $parentId) {
      id
      type
      name
      code
      description
      address
      serialNumber
      deviceId
      totalSlots
      availableSlots
      enabled
      metadata
      polygon
      createdAt
      updatedAt
      coordinates {
        latitude
        longitude
      }
      organization {
        id
        name
      }
      parent {
        id
        name
        type
        code
      }
      children {
        id
        name
        type
        code
      }
    }
  }
`;

// ==================== PRICING TEMPLATES ====================
export const GET_ALL_PRICING_TEMPLATES = gql`
  query GetAllPricingTemplates {
    getAllPricingTemplates {
      id
      organizationId
      organization {
        id
        name
      }
      name
      baseRate
      currency
      firstHourFree
      maxDailyRate
      hourlyRates
      pricingMode
      flatDayRate
      flatNightRate
      nightStartHour
      dayStartHour
      enabled
      assignmentCount
    }
  }
`;

export const GET_PRICING_TEMPLATE = gql`
  query GetPricingTemplate($id: ID!) {
    getPricingTemplate(id: $id) {
      id
      name
      baseRate
      currency
      firstHourFree
      maxDailyRate
      hourlyRates
      pricingMode
      flatDayRate
      flatNightRate
      nightStartHour
      dayStartHour
      enabled
      assignmentCount
      assignments {
        id
        location {
          id
          name
          type
        }
        validFrom
        validUntil
        enabled
        priority
      }
    }
  }
`;

// ==================== PRICING ASSIGNMENTS ====================
export const GET_PRICING_ASSIGNMENTS = gql`
  query GetPricingAssignments($locationId: ID) {
    getPricingAssignments(locationId: $locationId) {
      id
      validFrom
      validUntil
      enabled
      priority
      location {
        id
        name
        type
      }
      pricingTemplate {
        id
        name
        baseRate
        currency
        pricingMode
        flatDayRate
        flatNightRate
      }
    }
  }
`;

export const GET_LOCATION_PRICING_ASSIGNMENTS = gql`
  query GetLocationPricingAssignments($locationId: ID!) {
    getLocationPricingAssignments(locationId: $locationId) {
      id
      validFrom
      validUntil
      enabled
      priority
      pricingTemplate {
        id
        name
        baseRate
        currency
        pricingMode
        flatDayRate
        flatNightRate
      }
    }
  }
`;

// ==================== UNAUTHORIZED USAGE ====================
export const GET_UNAUTHORIZED_USAGE = gql`
  query GetUnauthorizedUsage(
    $parkingId: ID
    $sectorId: ID
    $slotId: ID
    $hoursBack: Int
    $includeResolved: Boolean
  ) {
    getUnauthorizedUsage(
      parkingId: $parkingId
      sectorId: $sectorId
      slotId: $slotId
      hoursBack: $hoursBack
      includeResolved: $includeResolved
    ) {
      id
      slotId
      slotName
      slotCode
      sectorId
      sectorName
      parkingId
      parkingName
      deviceId
      detectedAt
      resolvedAt
      durationMinutes
      isActive
    }
  }
`;
